/**
 * RFQs — files released to providers, derived read-only from the existing
 * `rfqProjects/{id}/hubFileReleases` (staged for suppliers) and
 * `rfqProjects/{id}/vendorFileReleases` (CNC vendor copies) subcollections plus
 * `rfqPackages` (which supplier received which staged release, and for which structures).
 *
 * No writes. Grouping is pure so the Files tab and tests share it.
 */

import { db } from './firebase';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type {
  ProjectFile,
  ProjectFileStream,
  ProjectFileStreamKind,
  RfqHubFileRelease,
  RfqPackage,
  RfqProject,
  RfqSubmission,
  VendorFileRelease,
  VendorWorkType,
} from '../types';
import type { StructureRef } from './proposalStructureRefs';
import { packageHubReleaseIds } from './rfqPackageReleases';
import { structureSelectionLabel, structureSetKey, WHOLE_PROJECT_SELECTION_LABEL } from './structureSelectionLabel';
import {
  approvedSubmissionIdsForReleaseSelection,
  approvedSupplierOrgIdsForReleaseSelection,
  releaseKeyForPackage,
} from './timberRfqPrePortalVersion';
import { cncAwardedSubmissionIdsForPackage } from './vendorPortalApprovals';

export const WHOLE_PROJECT_STRUCTURE_ID = structureSetKey([]);
export const WHOLE_PROJECT_LABEL = WHOLE_PROJECT_SELECTION_LABEL;
export const UNSENT_SUPPLIER_ID = '__unsent__';
export const UNSENT_SUPPLIER_LABEL = 'Staged — not sent to a supplier yet';

/** Timber lists / timber RFQs vs CNC quotes and vendor copies. */
export type RfqKindCategory = 'timber' | 'cnc';

export function rfqEntryKindCategory(entry: RfqFileEntry): RfqKindCategory {
  if (entry.kind === 'vendor' || entry.workType === 'cnc_quote') return 'cnc';
  return 'timber';
}

export function rfqKindCategoryLabel(category: RfqKindCategory): string {
  return category === 'cnc' ? 'CNC' : 'Timber';
}

export interface RfqFileEntry {
  /**
   * `hub` = staged supplier release, `vendor` = CNC vendor copy, `package` = a
   * supplier package that was published straight from the proposal (no staged
   * file behind it) — still a real send, so it must show in the history.
   */
  kind: 'hub' | 'vendor' | 'package';
  id: string;
  rfqProjectId: string;
  workType: VendorWorkType;
  displayLabel: string;
  fileName: string;
  releasedUrl: string;
  storagePath: string;
  mimeType?: string;
  sizeBytes?: number;
  releaseVersionIndex: number;
  versionIndex?: number;
  versionLabel?: string;
  sourceFileId: string;
  sourceStreamId: string;
  sourceStreamTitle: string;
  sourceStreamKind: ProjectFileStreamKind;
  releasedByName?: string;
  createdAt: Date;
  /** Packages (one per supplier) that delivered this staged release. Vendor copies have none. */
  packageIds: string[];
  linkedProposalVersionLabel?: string;
  /** Timber list came straight from the proposal (no Project Hub file behind it). */
  fromProposal?: boolean;
  /** Package-only entries: the supplier this package went to. */
  supplierOrgId?: string;
  /** Structure snapshot stamped on the staged release (hub entries). */
  structureIds?: string[];
  structureNames?: string[];
  /**
   * When a supplier answered one send with several quotes, each naming its structures
   * (`quotedStructures`), the send is listed once per quoted scope and each row covers only
   * these submissions. Absent = every submission on the row's packages.
   */
  submissionIds?: string[];
}

export interface RfqSupplierGroup {
  supplierOrgId: string;
  supplierName: string;
  entries: RfqFileEntry[];
}

/**
 * One group per RFQ *scope* — the set of structures a quote request covered
 * (e.g. "Ext. Accents" and "Lower Canopy + Main Porch +2"), not one per structure.
 * `structureId` is the set key ({@link structureSetKey}); `structureName` the label.
 */
export interface RfqStructureGroup {
  structureId: string;
  structureName: string;
  /** Structures in this scope (empty = whole project). */
  structureIds: string[];
  structureNames: string[];
  suppliers: RfqSupplierGroup[];
  total: number;
}

export interface BuildRfqGroupsInput {
  hubReleases: RfqHubFileRelease[];
  vendorReleases: VendorFileRelease[];
  packages: RfqPackage[];
  streams: ProjectFileStream[];
  files: ProjectFile[];
  structures: StructureRef[];
  /** supplierOrgId → display name. */
  supplierNames: Map<string, string>;
  /** Quote submissions for the deal — lets a send be split by the structures each quote covered. */
  submissions?: RfqSubmission[];
}

function hubEntry(r: RfqHubFileRelease, packageIds: string[]): RfqFileEntry {
  return {
    kind: 'hub',
    id: r.id ?? '',
    rfqProjectId: r.rfqProjectId,
    workType: r.workType,
    displayLabel: r.displayLabel || r.sourceStreamTitle || r.fileName,
    fileName: r.fileName,
    releasedUrl: r.releasedUrl,
    storagePath: r.storagePath,
    mimeType: r.mimeType,
    sizeBytes: r.sizeBytes,
    releaseVersionIndex: r.releaseVersionIndex,
    versionIndex: r.versionIndex,
    versionLabel: r.versionLabel,
    sourceFileId: r.sourceFileId,
    sourceStreamId: r.sourceStreamId,
    sourceStreamTitle: r.sourceStreamTitle,
    sourceStreamKind: r.sourceStreamKind,
    releasedByName: r.releasedByName,
    createdAt: r.createdAt,
    packageIds,
    linkedProposalVersionLabel: r.linkedProposalVersionLabel,
    fromProposal: !r.sourceFileId,
    structureIds: r.structureIds?.length ? [...r.structureIds] : undefined,
    structureNames: r.structureNames?.length ? [...r.structureNames] : undefined,
  };
}

function proposalSourceTitle(p: RfqPackage): string {
  switch (p.timberListSource) {
    case 'proposal_live':
      return 'Proposal (live builder)';
    case 'proposal_saved_version':
      return 'Proposal (saved version)';
    case 'hub_csv':
      return 'Project Hub CSV';
    default:
      return p.legacyImport ? 'Legacy import' : 'Proposal';
  }
}

/** A supplier package with no staged release behind it, rendered as its own history row. */
export function packageOnlyEntry(p: RfqPackage): RfqFileEntry {
  return {
    kind: 'package',
    id: p.id ?? '',
    rfqProjectId: p.rfqProjectId,
    workType: p.workType ?? 'timber_rfq',
    displayLabel: p.releaseDisplayLabel || p.label || 'Timber list',
    fileName: '',
    releasedUrl: '',
    storagePath: '',
    releaseVersionIndex: typeof p.releaseVersionIndex === 'number' ? p.releaseVersionIndex : 0,
    versionLabel: p.linkedProposalVersionLabel,
    sourceFileId: '',
    sourceStreamId: '',
    sourceStreamTitle: proposalSourceTitle(p),
    sourceStreamKind: p.workType === 'cnc_quote' ? 'general' : 'timber_list_csv',
    releasedByName: p.createdByName,
    createdAt: p.createdAt,
    packageIds: p.id ? [p.id] : [],
    linkedProposalVersionLabel: p.linkedProposalVersionLabel,
    fromProposal: p.workType !== 'cnc_quote',
    supplierOrgId: p.supplierOrgId,
  };
}

function vendorEntry(r: VendorFileRelease, packageIds: string[] = []): RfqFileEntry {
  return {
    kind: 'vendor',
    id: r.id ?? '',
    rfqProjectId: r.rfqProjectId,
    workType: 'cnc_quote',
    displayLabel: r.displayLabel || r.sourceStreamTitle || r.fileName,
    fileName: r.fileName,
    releasedUrl: r.releasedUrl,
    storagePath: r.storagePath,
    mimeType: r.mimeType,
    sizeBytes: r.sizeBytes,
    releaseVersionIndex: r.releaseVersionIndex,
    versionIndex: r.versionIndex,
    versionLabel: r.versionLabel,
    sourceFileId: r.sourceFileId,
    sourceStreamId: r.sourceStreamId,
    sourceStreamTitle: r.sourceStreamTitle,
    sourceStreamKind: 'general',
    releasedByName: r.releasedByName,
    createdAt: r.createdAt,
    packageIds,
  };
}

function packageIdsLinkedToVendorRelease(
  releaseId: string,
  supplierOrgId: string,
  packages: RfqPackage[],
): string[] {
  if (!releaseId || !supplierOrgId) return [];
  return packages
    .filter(
      p =>
        p.supplierOrgId === supplierOrgId
        && (p.cncPackagePayload?.releasedFileIds?.includes(releaseId) ?? false),
    )
    .map(p => p.id)
    .filter((id): id is string => !!id);
}

/** Other Quotes live on the Suppliers tab only — excluded from the RFQs file view. */
export function isOtherQuoteWorkType(workType: VendorWorkType | string | undefined): boolean {
  return workType === 'other_quote';
}

function submissionsByPackageId(submissions: RfqSubmission[]): Map<string, RfqSubmission[]> {
  const out = new Map<string, RfqSubmission[]>();
  for (const sub of submissions) {
    if (!sub.rfqPackageId) continue;
    const list = out.get(sub.rfqPackageId) ?? [];
    list.push(sub);
    out.set(sub.rfqPackageId, list);
  }
  return out;
}

function activeQuoteSubmissions(subs: RfqSubmission[]): RfqSubmission[] {
  return subs.filter(s => !s.notBidding && (!!s.storagePath || !!s.cncQuote));
}

const normalizeStructureName = (name: string) => name.trim().toLowerCase();

/**
 * Structures a published package was scoped to. `scopeStructures` is the explicit
 * record; older sends carry the scope implicitly — CNC quotes in the payload's
 * structure list, timber RFQs in the proposal snapshot they were cut from (one
 * nodeSet per structure). Snapshot nodeSets published from a hub CSV get
 * synthetic ids, so they are matched to the proposal's structures by name.
 */
export function packageScopeStructureIds(
  p: RfqPackage,
  structures: ReadonlyArray<StructureRef> = [],
): string[] {
  const explicit = (p.scopeStructures ?? []).map(s => s?.id).filter((id): id is string => !!id);
  if (explicit.length > 0) return explicit;
  const cnc = (p.cncPackagePayload?.structures ?? []).map(s => s?.id).filter((id): id is string => !!id);
  if (cnc.length > 0) return cnc;
  const nodeSets = p.proposalSnapshot?.nodeSets ?? [];
  if (nodeSets.length === 0 || structures.length === 0) return [];
  const knownIds = new Set(structures.map(s => s.id));
  const idByName = new Map(structures.map(s => [normalizeStructureName(s.name), s.id]));
  const resolved: string[] = [];
  for (const ns of nodeSets) {
    const id = ns?.id && knownIds.has(ns.id) ? ns.id : idByName.get(normalizeStructureName(String(ns?.name || '')));
    // One unmatched nodeSet (e.g. a single "Tyne Porch" whole-job list) means the
    // snapshot predates structures; treat the send as whole-project.
    if (!id) return [];
    resolved.push(id);
  }
  return resolved;
}

/**
 * Structures an entry belongs to, in priority order: the supplier packages' scope,
 * then the snapshot stamped on the staged release, then the source track's
 * current structure tags, then the CNC version's structure claim.
 * Empty = whole project.
 */
export function resolveEntryStructureIds(
  entry: RfqFileEntry,
  packagesForEntry: RfqPackage[],
  streamById: Map<string, ProjectFileStream>,
  fileById: Map<string, ProjectFile>,
  structures: ReadonlyArray<StructureRef> = [],
): string[] {
  const fromPackages = new Set<string>();
  for (const p of packagesForEntry) {
    for (const id of packageScopeStructureIds(p, structures)) fromPackages.add(id);
  }
  if (fromPackages.size > 0) return Array.from(fromPackages);
  if (entry.structureIds?.length) return [...entry.structureIds];
  const stream = streamById.get(entry.sourceStreamId);
  if (stream?.structureIds?.length) return [...stream.structureIds];
  const file = fileById.get(entry.sourceFileId);
  if (file?.cncStructureIds?.length) return [...file.cncStructureIds];
  return [];
}

/**
 * Group RFQ files by *request scope* → supplier. A scope is the set of structures
 * a quote request covered, so a job quoted as "Ext. Accents" plus "everything
 * else" shows exactly two groups — never one per structure. Each supplier's
 * packages decide the scope for that supplier; a release sent to two suppliers
 * for different scopes appears once under each. "Whole project" collects untagged
 * sends. Staged releases nobody has been sent yet sit under
 * {@link UNSENT_SUPPLIER_ID} (scoped by the staged snapshot / track tags) so staff
 * can see what still needs publishing. Entries are newest first.
 */
export function buildRfqGroups(rawInput: BuildRfqGroupsInput): RfqStructureGroup[] {
  // Other Quotes (steel connectors, misc. vendor quotes) are managed on the
  // Suppliers tab and never carry a hub file — they are not RFQ file history.
  const input: BuildRfqGroupsInput = {
    ...rawInput,
    packages: rawInput.packages.filter(p => !isOtherQuoteWorkType(p.workType)),
    submissions: (rawInput.submissions ?? []).filter(s => !isOtherQuoteWorkType(s.workType)),
  };
  const streamById = new Map<string, ProjectFileStream>();
  for (const s of input.streams) if (s.id) streamById.set(s.id, s);
  const fileById = new Map<string, ProjectFile>();
  for (const f of input.files) if (f.id) fileById.set(f.id, f);
  const structureName = new Map<string, string>(input.structures.map(s => [s.id, s.name]));
  for (const s of input.streams) {
    (s.structureIds ?? []).forEach((id, i) => {
      if (!structureName.has(id) && s.structureNames?.[i]) structureName.set(id, s.structureNames[i]);
    });
  }
  for (const p of input.packages) {
    for (const s of p.scopeStructures ?? []) if (s?.id && s.name && !structureName.has(s.id)) structureName.set(s.id, s.name);
  }
  for (const r of input.hubReleases) {
    (r.structureIds ?? []).forEach((id, i) => {
      if (!structureName.has(id) && r.structureNames?.[i]) structureName.set(id, r.structureNames[i]);
    });
  }
  const submissions = input.submissions ?? [];
  for (const sub of submissions) {
    for (const s of sub.quotedStructures ?? []) if (s?.id && s.name && !structureName.has(s.id)) structureName.set(s.id, s.name);
  }
  const subsByPkg = submissionsByPackageId(submissions);

  const packagesByHubRelease = new Map<string, RfqPackage[]>();
  for (const p of input.packages) {
    for (const releaseId of packageHubReleaseIds(p)) {
      const list = packagesByHubRelease.get(releaseId) ?? [];
      list.push(p);
      packagesByHubRelease.set(releaseId, list);
    }
  }

  // scopeKey → supplierOrgId → entries, plus the ids behind each scope key.
  const tree = new Map<string, Map<string, RfqFileEntry[]>>();
  const scopeIds = new Map<string, string[]>();
  const allStructureIds = input.structures.map(s => s.id);
  const coversWholeProject = (ids: string[]) =>
    allStructureIds.length > 0 && allStructureIds.every(id => ids.includes(id));
  const resolve = (entry: RfqFileEntry, pkgs: RfqPackage[]) => {
    const ids = resolveEntryStructureIds(entry, pkgs, streamById, fileById, input.structures);
    // A request that covered every structure on the proposal is the whole job.
    return coversWholeProject(ids) ? [] : ids;
  };
  const place = (structureIds: string[], supplierOrgId: string, entry: RfqFileEntry) => {
    const key = structureSetKey(structureIds);
    if (!scopeIds.has(key)) {
      scopeIds.set(key, Array.from(new Set(structureIds.filter(Boolean))).sort());
    }
    const bySupplier = tree.get(key) ?? new Map<string, RfqFileEntry[]>();
    const list = bySupplier.get(supplierOrgId) ?? [];
    list.push(entry);
    bySupplier.set(supplierOrgId, list);
    tree.set(key, bySupplier);
  };
  /**
   * List one supplier's send. Normally one row under the packages' scope. When the
   * supplier answered with quotes that each name their structures (a whole-project CNC
   * package quoted as "Ext. Accents" plus "the porches"), the send is listed once per
   * quoted scope instead, each row tied to its own submissions. Quotes that did not
   * name structures stay under the packages' scope.
   */
  const placeSupplierSend = (
    makeEntry: (packageIds: string[]) => RfqFileEntry,
    pkgs: RfqPackage[],
    supplierOrgId: string,
  ) => {
    const packageIds = pkgs.map(p => p.id!).filter(Boolean);
    const quotes = activeQuoteSubmissions(
      pkgs.flatMap(p => (p.id ? subsByPkg.get(p.id) ?? [] : [])),
    ).filter(s => s.id && s.supplierOrgId === supplierOrgId);
    const quotedIdsOf = (s: RfqSubmission) =>
      (s.quotedStructures ?? []).map(x => x?.id).filter((id): id is string => !!id);
    const scoped = quotes.filter(s => quotedIdsOf(s).length > 0);
    if (scoped.length === 0 || supplierOrgId === UNSENT_SUPPLIER_ID) {
      const entry = makeEntry(packageIds);
      place(resolve(entry, pkgs), supplierOrgId, entry);
      return;
    }
    const byQuotedScope = new Map<string, { ids: string[]; submissionIds: string[] }>();
    for (const s of scoped) {
      const ids = coversWholeProject(quotedIdsOf(s)) ? [] : quotedIdsOf(s);
      const key = structureSetKey(ids);
      const bucket = byQuotedScope.get(key) ?? { ids, submissionIds: [] };
      bucket.submissionIds.push(s.id!);
      byQuotedScope.set(key, bucket);
    }
    byQuotedScope.forEach(({ ids, submissionIds }) => {
      const entry = { ...makeEntry(packageIds), submissionIds };
      place(ids, supplierOrgId, entry);
    });
    const fullScope = quotes.filter(s => quotedIdsOf(s).length === 0).map(s => s.id!);
    if (fullScope.length > 0) {
      const entry = { ...makeEntry(packageIds), submissionIds: fullScope };
      place(resolve(entry, pkgs), supplierOrgId, entry);
    }
  };

  for (const r of input.hubReleases) {
    const pkgs = (r.id ? packagesByHubRelease.get(r.id) : undefined) ?? [];
    if (pkgs.length === 0) {
      const entry = hubEntry(r, []);
      // Staged, not sent: scope from the staged snapshot / track tags.
      place(resolve(entry, []), UNSENT_SUPPLIER_ID, entry);
      continue;
    }
    // Each supplier's own packages define the scope they were asked to quote.
    const bySupplier = new Map<string, RfqPackage[]>();
    for (const p of pkgs) {
      if (!p.supplierOrgId) continue;
      const list = bySupplier.get(p.supplierOrgId) ?? [];
      list.push(p);
      bySupplier.set(p.supplierOrgId, list);
    }
    if (bySupplier.size === 0) {
      const entry = hubEntry(
        r,
        pkgs.map(p => p.id!).filter(Boolean),
      );
      place(resolve(entry, pkgs), UNSENT_SUPPLIER_ID, entry);
      continue;
    }
    bySupplier.forEach((supplierPkgs, supplierOrgId) => {
      // Same supplier can receive one release for multiple structure scopes (separate packages).
      const byScope = new Map<string, RfqPackage[]>();
      for (const p of supplierPkgs) {
        const probe = hubEntry(r, p.id ? [p.id] : []);
        const scopeKey = structureSetKey(resolve(probe, [p]));
        const list = byScope.get(scopeKey) ?? [];
        list.push(p);
        byScope.set(scopeKey, list);
      }
      byScope.forEach(scopePkgs => {
        placeSupplierSend(ids => hubEntry(r, ids), scopePkgs, supplierOrgId);
      });
    });
  }
  const knownHubReleaseIds = new Set(input.hubReleases.map(r => r.id).filter(Boolean));
  for (const r of input.vendorReleases) {
    const supplierOrgId = r.supplierOrgId || UNSENT_SUPPLIER_ID;
    const linkedIds = packageIdsLinkedToVendorRelease(r.id ?? '', r.supplierOrgId || '', input.packages);
    const linkedPkgs = linkedIds.length ? input.packages.filter(p => p.id && linkedIds.includes(p.id)) : [];
    if (linkedPkgs.length === 0) {
      const entry = vendorEntry(r, []);
      place(resolve(entry, []), supplierOrgId, entry);
      continue;
    }
    // The vendor copy is the delivered copy of a staged release. When that release is
    // already listed for this supplier, listing the copy too would show the same send
    // (and its approval) twice.
    if (linkedPkgs.some(p => packageHubReleaseIds(p).some(id => knownHubReleaseIds.has(id)))) continue;
    placeSupplierSend(ids => vendorEntry(r, ids), linkedPkgs, supplierOrgId);
  }
  // Packages published without a staged file (proposal push / legacy import), or
  // whose staged release is not part of this project's feed: still a send.
  for (const p of input.packages) {
    if (packageHubReleaseIds(p).some(id => knownHubReleaseIds.has(id))) continue;
    placeSupplierSend(() => packageOnlyEntry(p), [p], p.supplierOrgId || UNSENT_SUPPLIER_ID);
  }

  const byTime = (a: RfqFileEntry, b: RfqFileEntry) => b.createdAt.getTime() - a.createdAt.getTime();
  const supplierLabel = (id: string) =>
    id === UNSENT_SUPPLIER_ID ? UNSENT_SUPPLIER_LABEL : input.supplierNames.get(id) || 'Supplier';
  const nameFor = (id: string) => structureName.get(id) || 'Structure';

  // Scope order: by the proposal position of each scope's first structure (then
  // alphabetical), unknown-structure scopes after, Whole project last.
  const proposalPos = new Map<string, number>(input.structures.map((s, i) => [s.id, i]));
  const rank = (key: string): [number, string] => {
    if (key === WHOLE_PROJECT_STRUCTURE_ID) return [Number.MAX_SAFE_INTEGER, ''];
    const ids = scopeIds.get(key) ?? [];
    const positions = ids.map(id => proposalPos.get(id)).filter((p): p is number => p != null);
    const first = positions.length ? Math.min(...positions) : Number.MAX_SAFE_INTEGER - 1;
    return [first, structureSelectionLabel(ids.map(nameFor))];
  };
  const orderedKeys = Array.from(tree.keys()).sort((a, b) => {
    const [pa, la] = rank(a);
    const [pb, lb] = rank(b);
    return pa !== pb ? pa - pb : la.localeCompare(lb);
  });

  return orderedKeys.map(key => {
    const bySupplier = tree.get(key)!;
    const ids = scopeIds.get(key) ?? [];
    const names = ids.map(nameFor);
    const suppliers: RfqSupplierGroup[] = Array.from(bySupplier.entries())
      .map(([supplierOrgId, entries]) => ({
        supplierOrgId,
        supplierName: supplierLabel(supplierOrgId),
        entries: [...entries].sort(byTime),
      }))
      .sort((a, b) => {
        if (a.supplierOrgId === UNSENT_SUPPLIER_ID) return 1;
        if (b.supplierOrgId === UNSENT_SUPPLIER_ID) return -1;
        return a.supplierName.localeCompare(b.supplierName);
      });
    const total = suppliers.reduce((n, s) => n + s.entries.length, 0);
    return {
      structureId: key,
      structureName: key === WHOLE_PROJECT_STRUCTURE_ID ? WHOLE_PROJECT_LABEL : structureSelectionLabel(names, { maxNamed: 3 }),
      structureIds: ids,
      structureNames: names,
      suppliers,
      total,
    };
  });
}

/**
 * Which RFQ scope sections start collapsed. Once a project has structure-scoped
 * sends, the "Whole project" section is usually pre-split history, so it starts
 * folded and the structure sections stay open. Single-scope projects keep
 * everything open.
 */
export function defaultCollapsedRfqGroupKeys(groups: RfqStructureGroup[]): Set<string> {
  const hasStructureScope = groups.some(g => g.structureIds.length > 0);
  const out = new Set<string>();
  if (!hasStructureScope) return out;
  for (const g of groups) {
    if (g.structureIds.length === 0) out.add(g.structureId);
  }
  return out;
}

// ── Supplier response status ────────────────────────────────────────────────

export type RfqSupplierStatus = 'sent' | 'quoted' | 'approved' | 'not_bidding';

export const RFQ_SUPPLIER_STATUS_LABELS: Record<RfqSupplierStatus, string> = {
  sent: 'Sent',
  quoted: 'Quoted',
  approved: 'Approved',
  not_bidding: 'Not bidding',
};

/** The slice of an RFQ work order needed to tell whether a quote was approved. */
export type RfqProjectApprovalFields = Pick<
  RfqProject,
  | 'id'
  | 'approvedSupplierOrgId'
  | 'approvedSubmissionIds'
  | 'approvedSubmissionIdsByRelease'
  | 'approvedSupplierOrgIdByRelease'
  | 'approvedSupplierOrgIdsByRelease'
>;

export interface RfqSupplierStatusInput {
  entry: RfqFileEntry;
  supplierOrgId: string;
  packages: RfqPackage[];
  submissions: RfqSubmission[];
  projectById: Map<string, RfqProjectApprovalFields>;
}

/**
 * Where one supplier stands on one sent file: Approved beats Quoted beats
 * Not bidding beats Sent. `null` when the file was never sent to that supplier
 * (staged only, or a CNC vendor copy with no package).
 *
 * Resolves approvals the same way the CNC/timber supplier panels do: per release
 * and per package, including structure-scoped CNC uploads on one version.
 */
export function rfqSupplierStatus(input: RfqSupplierStatusInput): RfqSupplierStatus | null {
  const { entry, supplierOrgId } = input;
  if (!supplierOrgId || supplierOrgId === UNSENT_SUPPLIER_ID) return null;
  const pkgIds = new Set(entry.packageIds);
  let scopedPkgs = input.packages.filter(p => p.id && pkgIds.has(p.id) && p.supplierOrgId === supplierOrgId);
  if (!scopedPkgs.length && entry.kind === 'vendor' && entry.id) {
    const linkedIds = packageIdsLinkedToVendorRelease(entry.id, supplierOrgId, input.packages);
    scopedPkgs = input.packages.filter(p => p.id && linkedIds.includes(p.id) && p.supplierOrgId === supplierOrgId);
  }
  if (!scopedPkgs.length) return null;

  const subsByPkg = submissionsByPackageId(input.submissions);
  const onlyIds = entry.submissionIds?.length ? new Set(entry.submissionIds) : null;
  const subs: RfqSubmission[] = [];
  for (const pkg of scopedPkgs) {
    if (!pkg.id) continue;
    for (const sub of subsByPkg.get(pkg.id) ?? []) {
      if (sub.supplierOrgId !== supplierOrgId) continue;
      if (onlyIds && !(sub.id && onlyIds.has(sub.id))) continue;
      subs.push(sub);
    }
  }

  const projectId = entry.rfqProjectId || scopedPkgs[0]?.rfqProjectId || '';
  const project = projectId ? input.projectById.get(projectId) : undefined;

  for (const pkg of scopedPkgs) {
    if (!pkg.id) continue;
    const pkgSubs = activeQuoteSubmissions(subs.filter(s => s.rfqPackageId === pkg.id));
    const releaseKey = releaseKeyForPackage(pkg);
    const approvedForRelease = new Set(
      approvedSubmissionIdsForReleaseSelection(
        project ?? { approvedSubmissionIds: [], approvedSubmissionIdsByRelease: {} },
        input.packages,
        subsByPkg,
        releaseKey,
      ),
    );
    if (pkgSubs.some(s => s.id && approvedForRelease.has(s.id))) return 'approved';

    const workType = pkg.workType ?? entry.workType;
    if (workType === 'cnc_quote') {
      // Resolve awards against every quote on the package (so explicit per-structure
      // approvals are seen), then check whether one of this row's quotes won.
      const allPkgQuotes = activeQuoteSubmissions(
        (subsByPkg.get(pkg.id) ?? []).filter(s => s.supplierOrgId === supplierOrgId),
      );
      const awarded = cncAwardedSubmissionIdsForPackage(project ?? {}, pkg, supplierOrgId, allPkgQuotes);
      if (pkgSubs.some(s => s.id && awarded.has(s.id))) return 'approved';
    }
  }

  const quoted = activeQuoteSubmissions(subs);
  if (quoted.length) {
    const releaseKey =
      entry.kind === 'hub' && entry.id
        ? entry.id
        : scopedPkgs[0]
          ? releaseKeyForPackage(scopedPkgs[0])
          : null;
    const isCnc =
      entry.workType === 'cnc_quote' || scopedPkgs.some(p => (p.workType ?? entry.workType) === 'cnc_quote');
    // Org-level CNC awards cover the whole send; a row tied to specific structure quotes is
    // Approved only through its own submissions (handled above).
    if (
      isCnc
      && !onlyIds
      && releaseKey
      && project
      && approvedSupplierOrgIdsForReleaseSelection(project, releaseKey).includes(supplierOrgId)
    ) {
      return 'approved';
    }
    return 'quoted';
  }
  if (subs.some(s => s.notBidding)) return 'not_bidding';
  return 'sent';
}

/** Every quote submission for a deal (approved/quoted/not-bidding chips in the RFQs view). */
export function subscribeRfqSubmissionsForDeal(
  dealId: string,
  onData: (submissions: RfqSubmission[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(collection(db, 'rfqSubmissions'), where('dealId', '==', dealId));
  return onSnapshot(
    q,
    snap =>
      onData(
        snap.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            ...(data as Omit<RfqSubmission, 'id' | 'createdAt'>),
            createdAt: data.createdAt?.toDate?.() ?? new Date(),
          } as RfqSubmission;
        }),
      ),
    err => onError?.(err),
  );
}

/** Approval fields of every RFQ work order on a deal, keyed by work order id. */
export function subscribeRfqProjectApprovalsForDeal(
  dealId: string,
  onData: (projects: Map<string, RfqProjectApprovalFields>) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(collection(db, 'rfqProjects'), where('dealId', '==', dealId));
  return onSnapshot(
    q,
    snap => {
      const out = new Map<string, RfqProjectApprovalFields>();
      snap.docs.forEach(d => {
        const data = d.data() as Partial<RfqProjectApprovalFields>;
        out.set(d.id, {
          id: d.id,
          approvedSupplierOrgId: data.approvedSupplierOrgId,
          approvedSubmissionIds: data.approvedSubmissionIds,
          approvedSubmissionIdsByRelease: data.approvedSubmissionIdsByRelease,
          approvedSupplierOrgIdByRelease: data.approvedSupplierOrgIdByRelease,
          approvedSupplierOrgIdsByRelease: data.approvedSupplierOrgIdsByRelease,
        });
      });
      onData(out);
    },
    err => onError?.(err),
  );
}

/** Distinct files (not placements) across all groups. */
export function countRfqFiles(groups: RfqStructureGroup[]): number {
  const ids = new Set<string>();
  for (const g of groups) for (const s of g.suppliers) for (const e of s.entries) ids.add(`${e.kind}:${e.id}`);
  return ids.size;
}

// ── Subscriptions ───────────────────────────────────────────────────────────

function mapHubReleaseDoc(d: QueryDocumentSnapshot<DocumentData>): RfqHubFileRelease {
  const data = d.data();
  return {
    id: d.id,
    rfqProjectId: String(data.rfqProjectId || d.ref.parent.parent?.id || ''),
    dealId: String(data.dealId || ''),
    workType: data.workType === 'cnc_quote' ? 'cnc_quote' : 'timber_rfq',
    sourceProjectId: String(data.sourceProjectId || ''),
    sourceFileId: String(data.sourceFileId || ''),
    sourceStreamId: String(data.sourceStreamId || ''),
    sourceStreamKind: (data.sourceStreamKind as ProjectFileStreamKind) || 'general',
    sourceStreamTitle: String(data.sourceStreamTitle || ''),
    structureIds: Array.isArray(data.structureIds)
      ? data.structureIds.filter((x: unknown): x is string => typeof x === 'string' && !!x)
      : undefined,
    structureNames: Array.isArray(data.structureNames)
      ? data.structureNames.map((x: unknown) => String(x ?? ''))
      : undefined,
    versionIndex: typeof data.versionIndex === 'number' ? data.versionIndex : undefined,
    versionLabel: data.versionLabel ? String(data.versionLabel) : undefined,
    displayLabel: String(data.displayLabel || ''),
    releaseVersionIndex: typeof data.releaseVersionIndex === 'number' ? data.releaseVersionIndex : 0,
    storagePath: String(data.storagePath || ''),
    releasedUrl: String(data.releasedUrl || ''),
    fileName: String(data.fileName || ''),
    mimeType: data.mimeType ? String(data.mimeType) : undefined,
    sizeBytes: typeof data.sizeBytes === 'number' ? data.sizeBytes : undefined,
    releasedByUid: String(data.releasedByUid || ''),
    releasedByName: data.releasedByName ? String(data.releasedByName) : undefined,
    createdAt: data.createdAt?.toDate?.() ?? new Date(),
    linkedProposalVersionId: data.linkedProposalVersionId ? String(data.linkedProposalVersionId) : undefined,
    linkedProposalVersionLabel: data.linkedProposalVersionLabel
      ? String(data.linkedProposalVersionLabel)
      : undefined,
  };
}

function mapVendorReleaseDoc(d: QueryDocumentSnapshot<DocumentData>): VendorFileRelease {
  const data = d.data();
  return {
    id: d.id,
    rfqProjectId: String(data.rfqProjectId || d.ref.parent.parent?.id || ''),
    supplierOrgId: String(data.supplierOrgId || ''),
    dealId: String(data.dealId || ''),
    sourceFileId: String(data.sourceFileId || ''),
    sourceStreamId: String(data.sourceStreamId || ''),
    sourceStreamTitle: String(data.sourceStreamTitle || ''),
    sourceProjectId: String(data.sourceProjectId || ''),
    versionIndex: typeof data.versionIndex === 'number' ? data.versionIndex : undefined,
    versionLabel: data.versionLabel ? String(data.versionLabel) : undefined,
    displayLabel: String(data.displayLabel || ''),
    releaseVersionIndex: typeof data.releaseVersionIndex === 'number' ? data.releaseVersionIndex : 0,
    storagePath: String(data.storagePath || data.releasedStoragePath || ''),
    releasedUrl: String(data.releasedUrl || ''),
    fileName: String(data.fileName || data.name || ''),
    mimeType: data.mimeType ? String(data.mimeType) : undefined,
    sizeBytes:
      typeof data.sizeBytes === 'number' ? data.sizeBytes : typeof data.size === 'number' ? data.size : undefined,
    releasedByUid: String(data.releasedByUid || data.releasedBy || ''),
    releasedByName: data.releasedByName ? String(data.releasedByName) : undefined,
    createdAt: data.createdAt?.toDate?.() ?? data.releasedAt?.toDate?.() ?? new Date(),
  };
}

export interface RfqReleaseScope {
  /** Deal the RFQ work orders belong to (preferred — proposal pushes carry the deal id, not the hub project id). */
  dealId?: string | null;
  /** Fallback when the project has no deal: RFQ work orders linked to this timber project. */
  projectId?: string | null;
}

/**
 * Walk every `rfqProjects` work order for the deal and merge one subcollection
 * across them. Two-tier on purpose: it needs no composite index (unlike the
 * collection-group query it replaces), and it catches releases pushed straight
 * from the proposal, whose `sourceProjectId` is the deal id rather than the hub
 * project id. Emits once every work order has reported at least once.
 */
function subscribeDealRfqSubcollection<T extends { createdAt: Date }>(
  scope: RfqReleaseScope,
  subcollection: 'hubFileReleases' | 'vendorFileReleases',
  mapDoc: (d: QueryDocumentSnapshot<DocumentData>) => T,
  onData: (rows: T[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const dealId = (scope.dealId || '').trim();
  const projectId = (scope.projectId || '').trim();
  if (!dealId && !projectId) {
    onData([]);
    return () => {};
  }
  const projectsQuery = dealId
    ? query(collection(db, 'rfqProjects'), where('dealId', '==', dealId))
    : query(collection(db, 'rfqProjects'), where('timberProjectId', '==', projectId));

  const rowsByProject = new Map<string, T[]>();
  const unsubByProject = new Map<string, () => void>();
  const ready = new Set<string>();
  let projectIds: string[] = [];
  let closed = false;

  const emit = () => {
    if (closed) return;
    if (!projectIds.every(id => ready.has(id))) return;
    const merged: T[] = [];
    for (const id of projectIds) merged.push(...(rowsByProject.get(id) ?? []));
    merged.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    onData(merged);
  };

  const unsubProjects = onSnapshot(
    projectsQuery,
    snap => {
      projectIds = snap.docs.map(d => d.id);
      const live = new Set(projectIds);
      Array.from(unsubByProject.keys()).forEach(id => {
        if (live.has(id)) return;
        unsubByProject.get(id)?.();
        unsubByProject.delete(id);
        rowsByProject.delete(id);
        ready.delete(id);
      });
      for (const id of projectIds) {
        if (unsubByProject.has(id)) continue;
        const unsub = onSnapshot(
          collection(db, 'rfqProjects', id, subcollection),
          sub => {
            rowsByProject.set(id, sub.docs.map(mapDoc));
            ready.add(id);
            emit();
          },
          err => {
            // One broken work order should not blank the whole history.
            console.warn(`[projectRfqFiles] ${subcollection} feed for rfqProjects/${id}`, err);
            rowsByProject.set(id, []);
            ready.add(id);
            emit();
          },
        );
        unsubByProject.set(id, unsub);
      }
      if (projectIds.length === 0) onData([]);
      else emit();
    },
    err => onError?.(err),
  );

  return () => {
    closed = true;
    unsubProjects();
    unsubByProject.forEach(u => u());
    unsubByProject.clear();
  };
}

/** Every staged supplier release for this deal's RFQ work orders (hub-staged and proposal-pushed). */
export function subscribeProjectHubReleases(
  scope: RfqReleaseScope,
  onData: (releases: RfqHubFileRelease[]) => void,
  onError?: (err: Error) => void,
): () => void {
  return subscribeDealRfqSubcollection(scope, 'hubFileReleases', mapHubReleaseDoc, onData, onError);
}

/** Every CNC vendor copy for this deal's RFQ work orders. */
export function subscribeProjectVendorReleases(
  scope: RfqReleaseScope,
  onData: (releases: VendorFileRelease[]) => void,
  onError?: (err: Error) => void,
): () => void {
  return subscribeDealRfqSubcollection(scope, 'vendorFileReleases', mapVendorReleaseDoc, onData, onError);
}

/** Published supplier packages for a deal (which supplier got which staged release). */
export function subscribeRfqPackagesForDeal(
  dealId: string,
  onData: (packages: RfqPackage[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(collection(db, 'rfqPackages'), where('dealId', '==', dealId));
  return onSnapshot(
    q,
    snap =>
      onData(
        snap.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            ...(data as Omit<RfqPackage, 'id' | 'createdAt'>),
            createdAt: data.createdAt?.toDate?.() ?? new Date(),
            ...(data.quoteDeadline?.toDate ? { quoteDeadline: data.quoteDeadline.toDate() } : {}),
          } as RfqPackage;
        }),
      ),
    err => onError?.(err),
  );
}

const supplierNameCache = new Map<string, string>();

/** Resolve supplier org names (cached per session). Unknown ids resolve to "Supplier". */
export async function resolveSupplierOrgNames(ids: Iterable<string>): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const missing: string[] = [];
  Array.from(ids).forEach(id => {
    if (!id || id === UNSENT_SUPPLIER_ID) return;
    const cached = supplierNameCache.get(id);
    if (cached) out.set(id, cached);
    else missing.push(id);
  });
  await Promise.all(
    missing.map(async id => {
      try {
        const snap = await getDoc(doc(db, 'supplierOrgs', id));
        const name = snap.exists() ? String(snap.data()?.name || '') : '';
        const resolved = name || 'Supplier';
        supplierNameCache.set(id, resolved);
        out.set(id, resolved);
      } catch {
        out.set(id, 'Supplier');
      }
    }),
  );
  return out;
}
